// Composant ProgressBar réutilisable

import { type HTMLAttributes, forwardRef } from 'react'

export interface ProgressBarProps extends HTMLAttributes<HTMLDivElement> {
  value: number // 0-100
  max?: number
  size?: 'sm' | 'md' | 'lg'
  color?: 'primary' | 'success' | 'warning' | 'danger' | 'neutral'
  showLabel?: boolean
  labelPosition?: 'inside' | 'outside'
  animated?: boolean
}

const ProgressBar = forwardRef<HTMLDivElement, ProgressBarProps>(
  (
    {
      className = '',
      value,
      max = 100,
      size = 'md',
      color = 'primary',
      showLabel = false,
      labelPosition = 'outside',
      animated = false,
      ...props
    },
    ref
  ) => {
    const percentage = Math.min(Math.max((value / max) * 100, 0), 100)

    const sizeStyles = {
      sm: 'h-1.5',
      md: 'h-2.5',
      lg: 'h-4',
    }

    const colorStyles = {
      primary: 'bg-primary-600',
      success: 'bg-green-500',
      warning: 'bg-orange-500',
      danger: 'bg-red-500',
      neutral: 'bg-gray-500',
    }

    const labelSizeStyles = {
      sm: 'text-xs',
      md: 'text-sm',
      lg: 'text-base',
    }

    return (
      <div ref={ref} className={className} {...props}>
        {showLabel && labelPosition === 'outside' && (
          <div className="flex justify-between mb-1">
            <span className={`font-medium text-gray-700 ${labelSizeStyles[size]}`}>
              Progression
            </span>
            <span className={`font-medium text-gray-700 ${labelSizeStyles[size]}`}>
              {Math.round(percentage)}%
            </span>
          </div>
        )}

        <div
          className={`w-full bg-gray-200 rounded-full overflow-hidden ${sizeStyles[size]}`}
        >
          <div
            className={`
              ${sizeStyles[size]}
              ${colorStyles[color]}
              rounded-full
              transition-all duration-300 ease-out
              ${animated ? 'animate-pulse' : ''}
              ${showLabel && labelPosition === 'inside' ? 'flex items-center justify-center' : ''}
            `}
            style={{ width: `${percentage}%` }}
            role="progressbar"
            aria-valuenow={value}
            aria-valuemin={0}
            aria-valuemax={max}
          >
            {showLabel && labelPosition === 'inside' && size === 'lg' && (
              <span className="text-xs font-medium text-white">
                {Math.round(percentage)}%
              </span>
            )}
          </div>
        </div>
      </div>
    )
  }
)

ProgressBar.displayName = 'ProgressBar'

// Composant multi-barres pour les axes HALTES
export interface MultiProgressProps {
  segments: Array<{
    label: string
    value: number
    color: 'primary' | 'success' | 'warning' | 'danger' | 'neutral'
  }>
  size?: 'sm' | 'md' | 'lg'
}

export function MultiProgressBar({
  segments,
  size = 'md',
}: MultiProgressProps) {
  const sizeStyles = {
    sm: 'h-1.5',
    md: 'h-2.5',
    lg: 'h-4',
  }

  const colorStyles = {
    primary: 'bg-primary-600',
    success: 'bg-green-500',
    warning: 'bg-orange-500',
    danger: 'bg-red-500',
    neutral: 'bg-gray-500',
  }

  return (
    <div className="space-y-2">
      {segments.map((segment, index) => (
        <div key={index}>
          <div className="flex justify-between mb-1">
            <span className="text-sm font-medium text-gray-700">
              {segment.label}
            </span>
            <span className="text-sm font-medium text-gray-700">
              {segment.value}%
            </span>
          </div>
          <div
            className={`w-full bg-gray-200 rounded-full overflow-hidden ${sizeStyles[size]}`}
          >
            <div
              className={`${sizeStyles[size]} ${colorStyles[segment.color]} rounded-full transition-all duration-300`}
              style={{ width: `${segment.value}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

export default ProgressBar
